// SPDX-License-Identifier: AGPL-3.0-or-later

import {APIErrorCodes} from '@fluxer/constants/src/ApiErrorCodes';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';

export const CHARACTERS_CHANGED_DESCRIPTOR = msg({
	message: 'Those characters changed. Reopen the list and save again.',
	comment:
		"Toast when saving a message's characters fails because one is no longer the author's or no longer in the channel.",
});
export const OWNER_NOT_LINKED_DESCRIPTOR = msg({
	message: "This message's author has no characters on the site.",
	comment: "Toast when changing a message's characters fails because its author has no linked character account.",
});
export const NO_PRIMARY_CHARACTER_DESCRIPTOR = msg({
	message: 'The author has no primary character here. Use Manage characters to choose one.',
	comment:
		'Toast when marking a message in-character fails because its author has no primary character in the channel. "Manage characters" is the name of the message menu entry.',
});
export const MESSAGE_DELETED_DESCRIPTOR = msg({
	message: 'That message was deleted.',
	comment: "Toast when changing a message's characters fails because the message no longer exists.",
});
export const RATE_LIMITED_DESCRIPTOR = msg({
	message: 'Too many changes at once. Try again in a few seconds.',
	comment: "Toast when changing a message's characters is rate limited.",
});
export const CAST_UNAVAILABLE_DESCRIPTOR = msg({
	message: "Couldn't reach the character site. Try again.",
	comment: "Toast when changing a message's characters fails because the personal character site is unavailable.",
});
export const CHARACTERS_UPDATE_FAILED_DESCRIPTOR = msg({
	message: "The message's characters didn't change. Try again.",
	comment:
		"Toast when changing a message's characters (or marking it in or out of character) fails for another reason.",
});

export const SENT_OOC_OWNER_NOT_LINKED_DESCRIPTOR = msg({
	message: 'Sent out of character: your account has no characters on the site.',
	comment:
		'Toast after sending a message in character when it went out of character because the account has no linked characters. The message was sent.',
});
export const SENT_OOC_NO_PRIMARY_DESCRIPTOR = msg({
	message: 'Sent out of character: you have no primary character in this channel.',
	comment:
		'Toast after sending a message in character when it went out of character because the sender has no primary character in the channel. The message was sent.',
});
export const SENT_OOC_CHARACTERS_UNAVAILABLE_DESCRIPTOR = msg({
	message: "Sent out of character: those characters aren't available here.",
	comment:
		"Toast after sending a message in character when it went out of character because the chosen characters aren't the sender's or aren't in the channel. The message was sent.",
});
export const SENT_OOC_CAST_UNAVAILABLE_DESCRIPTOR = msg({
	message: "Sent out of character: couldn't reach the character site.",
	comment:
		'Toast after sending a message in character when it went out of character because the personal character site was unavailable. The message was sent.',
});
export const SENT_OOC_NOT_IN_GUILD_DESCRIPTOR = msg({
	message: 'Sent out of character: characters only work in community channels.',
	comment:
		'Toast after sending a message in character in a channel outside a community (a DM), so it went out of character. The message was sent.',
});
export const SENT_OOC_DESCRIPTOR = msg({
	message: 'Sent out of character.',
	comment:
		'Toast after sending a message in character when it went out of character for a reason the app does not know. The message was sent.',
});

interface ManageMessageCharactersContext {
	isUserMessage: boolean;
	/** The message's channel's guild: messages hydrated from REST history carry no guild_id of their own. */
	guildId: string | null | undefined;
	canManageGuild: boolean;
}

/**
 * Whether the message menu offers "Manage characters". Offered on in- and out-of-character messages
 * alike (saving characters on an OOC message makes it in-character as them), in guild channels only,
 * and only to members with MANAGE_GUILD: the picker lists the author's characters through the
 * owner-accounts route, which the server gates on it, so without it the picker could only show an
 * empty list.
 */
export function canManageMessageCharacters({
	isUserMessage,
	guildId,
	canManageGuild,
}: ManageMessageCharactersContext): boolean {
	return isUserMessage && guildId != null && canManageGuild;
}

/**
 * The toast text for a failed PATCH /channels/:id/messages/:id/ic, from the picker or the IC toggle.
 * Reads the HttpError shape (status, body.code) structurally so any rejection is safe to pass in.
 */
export function describeMessageIcError(error: unknown): MessageDescriptor {
	const status =
		typeof error === 'object' && error !== null && typeof (error as {status?: unknown}).status === 'number'
			? (error as {status: number}).status
			: undefined;
	const body = typeof error === 'object' && error !== null ? (error as {body?: unknown}).body : undefined;
	const code = typeof body === 'object' && body !== null ? (body as {code?: unknown}).code : undefined;
	switch (code) {
		case APIErrorCodes.CAST_CHARACTER_NOT_OWNED:
		case APIErrorCodes.CAST_CHARACTER_NOT_IN_CHANNEL:
			return CHARACTERS_CHANGED_DESCRIPTOR;
		case APIErrorCodes.CAST_OWNER_NOT_LINKED:
			return OWNER_NOT_LINKED_DESCRIPTOR;
		case APIErrorCodes.CAST_NO_PRIMARY_CHARACTER:
			return NO_PRIMARY_CHARACTER_DESCRIPTOR;
		case APIErrorCodes.UNKNOWN_MESSAGE:
			return MESSAGE_DELETED_DESCRIPTOR;
	}
	if (status === 429) {
		return RATE_LIMITED_DESCRIPTOR;
	}
	if (status === 404) {
		return MESSAGE_DELETED_DESCRIPTOR;
	}
	if (status === 502) {
		return CAST_UNAVAILABLE_DESCRIPTOR;
	}
	return CHARACTERS_UPDATE_FAILED_DESCRIPTOR;
}

/**
 * The toast text when a message sent in character went out of character, read from the send
 * response's `ic_fallback_reason` (the server sets it only on that response, and only then); null
 * when the response has none. Read structurally so any response body is safe to pass in.
 */
export function describeIcSendFallback(message: unknown): MessageDescriptor | null {
	const reason =
		typeof message === 'object' && message !== null
			? (message as {ic_fallback_reason?: unknown}).ic_fallback_reason
			: undefined;
	if (typeof reason !== 'string') {
		return null;
	}
	switch (reason) {
		case APIErrorCodes.CAST_OWNER_NOT_LINKED:
			return SENT_OOC_OWNER_NOT_LINKED_DESCRIPTOR;
		case APIErrorCodes.CAST_NO_PRIMARY_CHARACTER:
			return SENT_OOC_NO_PRIMARY_DESCRIPTOR;
		case APIErrorCodes.CAST_CHARACTER_NOT_OWNED:
		case APIErrorCodes.CAST_CHARACTER_NOT_IN_CHANNEL:
			return SENT_OOC_CHARACTERS_UNAVAILABLE_DESCRIPTOR;
		case 'CAST_UNAVAILABLE':
			return SENT_OOC_CAST_UNAVAILABLE_DESCRIPTOR;
		case 'CAST_NOT_IN_GUILD':
			return SENT_OOC_NOT_IN_GUILD_DESCRIPTOR;
	}
	return SENT_OOC_DESCRIPTOR;
}
